import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { QrCode, Users, CalendarCheck, FlaskConical, RefreshCw, Mail, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "@/lib/api";

const card = "rounded-2xl bg-white border border-slate-200 p-5";
const pct = (v) => (v == null ? "—" : `${Math.round(v * 1000) / 10}%`);
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function HeroStat({ icon: Icon, label, value, sub, accent, tid }) {
  return (
    <div className={`${card} flex items-start gap-4`} data-testid={tid}>
      <div className={`rounded-xl p-3 ${accent}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-4xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mt-2">{label}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

const ACTIVITY_META = {
  scan:        { label: "QR Scan",      cls: "bg-amber-100 text-amber-800",    Icon: QrCode },
  lead:        { label: "Lead",         cls: "bg-cyan-100 text-cyan-800",      Icon: Users },
  booking:     { label: "Appointment",  cls: "bg-emerald-100 text-emerald-800", Icon: CalendarCheck },
  lab_booking: { label: "Test Booking", cls: "bg-emerald-600 text-white",      Icon: FlaskConical },
};

export default function ResponseDashboard() {
  const [campaigns, setCampaigns] = useState(null);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/admin/property-campaigns").then((r) => {
      const items = r.data.campaigns || [];
      setCampaigns(items);
      const mailed = items.filter((c) => c.mailed_at)
        .sort((a, b) => (b.mailed_at || "").localeCompare(a.mailed_at || ""));
      setCid((mailed[0] || items[0])?.id || "");
    }).catch(() => setCampaigns([]));
  }, []);

  const load = useCallback((id, quiet = false) => {
    if (!id) return;
    if (!quiet) setBusy(true);
    api.get(`/admin/property-campaigns/${id}/response-dashboard`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    setData(null);
    load(cid);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => load(cid, true), 30000);
    return () => clearInterval(timerRef.current);
  }, [cid, load]);

  const s = data?.stats || {};
  const chartDays = (data?.timeline || []).slice(-30);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="response-dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">Response Dashboard</h1>
            <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="rd-live-indicator">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              LIVE · 30s refresh
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {data?.campaign ? <>Campaign <span className="font-semibold text-slate-700">{data.campaign.code}</span>{data.campaign.mailed_at ? ` · mailed ${fmtDT(data.campaign.mailed_at)}` : " · not mailed yet"} · updated {fmtDT(data.generated_at)}</> : "Loading…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="rd-campaign-select"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            {(campaigns || []).map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
          </select>
          <button onClick={() => load(cid)} disabled={busy} data-testid="rd-refresh-btn"
                  className="rounded-lg border border-slate-300 bg-white p-2 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 text-slate-600 ${busy ? "animate-spin" : ""}`} />
          </button>
          {data?.campaign && (
            <Link to={`/admin/property-campaigns/${data.campaign.id}`} data-testid="rd-manage-link"
                  className="text-sm font-semibold text-cyan-700 hover:underline whitespace-nowrap">Manage →</Link>
          )}
        </div>
      </div>

      {campaigns && campaigns.length === 0 && (
        <div className={`${card} text-slate-500`} data-testid="rd-empty">No campaigns yet — create one under Property Campaigns.</div>
      )}

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <HeroStat icon={QrCode} label="Unique Scans" value={s.unique_scanned ?? 0}
                      sub={`${s.total_scans ?? 0} total scans · ${pct(s.scan_rate)} of mailed`}
                      accent="bg-amber-100 text-amber-700" tid="rd-stat-scans" />
            <HeroStat icon={Users} label="Leads Captured" value={s.leads_captured ?? 0}
                      sub={`${pct(s.lead_rate)} of mailed · ${pct(s.scan_to_lead_rate)} of scans`}
                      accent="bg-cyan-100 text-cyan-700" tid="rd-stat-leads" />
            <HeroStat icon={CalendarCheck} label="Bookings" value={s.appointments_scheduled ?? 0}
                      sub={`${s.lab_tests_booked ?? 0} lab tests booked · ${s.bookings_created ?? 0} converted`}
                      accent="bg-emerald-100 text-emerald-700" tid="rd-stat-bookings" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            {[
              { l: "Letters Mailed", v: s.mailed ?? 0, I: Mail },
              { l: "Eligibility Answers", v: s.eligibility_checks ?? 0, I: TrendingUp },
              { l: "Inspections Done", v: s.inspections_completed ?? 0, I: CalendarCheck },
              { l: "Reports Delivered", v: s.reports_delivered ?? 0, I: FlaskConical },
            ].map(({ l, v, I }) => (
              <div key={l} className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3" data-testid={`rd-mini-${l.toLowerCase().replace(/ /g, "-")}`}>
                <I className="w-4 h-4 text-slate-400 shrink-0" />
                <div>
                  <div className="text-xl font-bold text-slate-900 leading-none">{v}</div>
                  <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">{l}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-5 gap-4 mt-4">
            <div className={`${card} lg:col-span-3`} data-testid="rd-timeline">
              <div className="text-sm font-semibold text-slate-700 mb-3">Daily activity (last 30 days)</div>
              {chartDays.length === 0 ? (
                <div className="text-sm text-slate-400 py-10 text-center">Timeline appears once the campaign is mailed or activity starts.</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartDays}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total_scans" name="Scans" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="leads" name="Leads" fill="#0891b2" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className={`${card} lg:col-span-2`} data-testid="rd-activity-feed">
              <div className="text-sm font-semibold text-slate-700 mb-3">Recent activity</div>
              {(data.recent || []).length === 0 ? (
                <div className="text-sm text-slate-400 py-10 text-center">No scans, leads or bookings yet.</div>
              ) : (
                <ul className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                  {data.recent.map((e, idx) => {
                    const m = ACTIVITY_META[e.type] || ACTIVITY_META.scan;
                    return (
                      <li key={idx} className="flex items-center gap-3 text-sm" data-testid={`rd-activity-row-${idx}`}>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0 ${m.cls}`}>{m.label}</span>
                        <span className="text-slate-700 truncate flex-1">
                          {e.name ? `${e.name} · ` : ""}{e.address || "—"}{e.detail ? ` · ${e.detail}` : ""}
                        </span>
                        <span className="text-[11px] text-slate-400 shrink-0" title={fmtDT(e.at)}>{ago(e.at)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
